import React from 'react';
import * as Icons from 'lucide-react';
import { ShieldCheck, Smartphone, Anchor } from 'lucide-react';
import { workflowSteps, finalImage, features } from '../mock';

export function PillarsSection() {
  return (
    <section id="pillars" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-5">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-spin-dark">
            Der Spinsafe steht auf zwei soliden Schutzsäulen
          </h2>
          <hr className="section-divider" />
        </div>

        <div className="grid md:grid-cols-2 gap-10">
          {[
            {
              icon: <Smartphone size={64} strokeWidth={1.4} className="text-spin-green" />,
              title: 'Digitaler Schutz',
              text: 'Digitaler Schutz durch die enthaltene Smartphone Alarm-App | Immer up-to-date',
              link: { href: '/howto#apps', label: 'Mehr zur App' },
            },
            {
              icon: <Anchor size={64} strokeWidth={1.4} className="text-spin-green" />,
              title: 'Physischer Schutz',
              text: 'Physischer Schutz durch Bodenschraube und entkoppeltes Hardcase',
              link: { href: '#workflow', label: 'Mehr zum Safe' },
            },
          ].map((p) => (
            <div
              key={p.title}
              className="text-center p-8 rounded-lg bg-spin-sand/50 hover:bg-spin-sand transition-colors group"
            >
              <div className="flex justify-center mb-5 pillar-icon group-hover:scale-110 transition-transform">
                {p.icon}
              </div>
              <h3 className="text-xl font-heading font-bold text-spin-dark mb-3">{p.title}</h3>
              <p className="text-spin-grey leading-relaxed mb-5">{p.text}</p>
              <a
                href={p.link.href}
                className="inline-block px-5 py-2 border-2 border-spin-green text-spin-green font-semibold rounded hover:bg-spin-green hover:text-white transition-colors text-sm uppercase tracking-wide"
              >
                {p.link.label}
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function WorkflowSection() {
  return (
    <section id="workflow" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-5">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-spin-dark">
            So geht's
          </h2>
          <hr className="section-divider" />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 items-end">
          {workflowSteps.map((s) => (
            <div key={s.step} className="workflow-card flex flex-col items-center">
              {/* Full-height product image with no crop */}
              <div className="flex-1 w-full flex items-end justify-center pb-3 min-h-[420px]">
                <img
                  src={s.img}
                  alt={s.title}
                  className="max-h-[460px] w-auto object-contain transition-transform duration-500 hover:-translate-y-1"
                  loading="lazy"
                />
              </div>

              {/* Grey caption panel - fixed equal height */}
              <div className="w-full bg-spin-grey/85 rounded-md px-5 py-5 text-white text-center h-[170px] flex flex-col">
                <p className="text-base font-bold mb-2 self-start">
                  Schritt {s.step}:
                </p>
                <p className="text-[15px] leading-snug flex-1 flex items-center justify-center">
                  {s.title}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="inline-block rounded-lg overflow-hidden shadow-xl bg-white">
            <img
              src={finalImage.img}
              alt={finalImage.caption}
              className="max-w-md w-full"
              loading="lazy"
            />
          </div>
          <p className="mt-5 font-heading font-semibold text-spin-dark text-lg">
            {finalImage.caption}
          </p>
        </div>
      </div>
    </section>
  );
}

export function FeaturesSection() {
  return (
    <section id="features" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-5">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-spin-dark">
            Funktionen
          </h2>
          <hr className="section-divider" />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
          {features.map((f) => {
            const Icon = Icons[f.icon] || ShieldCheck;
            return (
              <div
                key={f.title}
                className="p-6 rounded-lg border border-border hover:border-spin-green hover:shadow-lg transition-all group bg-white"
              >
                <div className="w-12 h-12 rounded-md bg-spin-sand flex items-center justify-center mb-4 group-hover:bg-spin-green transition-colors">
                  <Icon size={22} className="text-spin-green group-hover:text-white transition-colors" strokeWidth={2} />
                </div>
                <h3 className="font-heading font-bold text-lg text-spin-dark mb-2">{f.title}</h3>
                <p className="text-spin-grey text-sm leading-relaxed">{f.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
